import { useEffect, useState } from "react";
import { api, formatNaira, formatDate } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Chip, statusChip } from "@/components/Chip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, ShoppingCart, Truck, Star } from "lucide-react";
import { toast } from "sonner";

export default function Procurement() {
  const [suppliers, setSuppliers] = useState([]);
  const [pos, setPos] = useState([]);
  const [sOpen, setSOpen] = useState(false);
  const [pOpen, setPOpen] = useState(false);
  const [sForm, setSForm] = useState({ name: "", contact_person: "", email: "", phone: "", category: "hardware", rating: 3, notes: "" });
  const [pForm, setPForm] = useState({ supplier_id: "", business_unit: "FigureWeb", items: [{ _uid: `li_${Date.now()}`, description: "", quantity: 1, rate: 0 }], notes: "", expected_delivery: "" });

  const load = () => {
    api.get("/suppliers").then(r => setSuppliers(r.data));
    api.get("/purchase-orders").then(r => setPos(r.data));
  };
  useEffect(() => { load(); }, []);

  const createSupplier = async () => {
    if (!sForm.name) return toast.error("Name required");
    await api.post("/suppliers", { ...sForm, rating: Number(sForm.rating) });
    toast.success("Supplier added");
    setSOpen(false);
    setSForm({ name: "", contact_person: "", email: "", phone: "", category: "hardware", rating: 3, notes: "" });
    load();
  };

  const createPO = async () => {
    if (!pForm.supplier_id) return toast.error("Select supplier");
    const items = pForm.items.map(it => ({ description: it.description, quantity: Number(it.quantity), rate: Number(it.rate) }));
    await api.post("/purchase-orders", { ...pForm, items });
    toast.success("Purchase order created");
    setPOpen(false);
    setPForm({ supplier_id: "", business_unit: "FigureWeb", items: [{ _uid: `li_${Date.now()}`, description: "", quantity: 1, rate: 0 }], notes: "", expected_delivery: "" });
    load();
  };

  const updateItem = (i, field, val) => {
    const items = [...pForm.items];
    items[i] = { ...items[i], [field]: field === "description" ? val : Number(val) };
    setPForm({ ...pForm, items });
  };

  const poTotal = pForm.items.reduce((s, it) => s + Number(it.quantity) * Number(it.rate), 0);

  return (
    <div className="space-y-6">
      <PageHeader title="Procurement" subtitle="Suppliers and purchase orders" />

      <Tabs defaultValue="suppliers">
        <TabsList>
          <TabsTrigger value="suppliers"><Truck className="w-4 h-4 mr-1.5" />Suppliers</TabsTrigger>
          <TabsTrigger value="pos"><ShoppingCart className="w-4 h-4 mr-1.5" />Purchase Orders</TabsTrigger>
        </TabsList>

        <TabsContent value="suppliers" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Dialog open={sOpen} onOpenChange={setSOpen}>
              <DialogTrigger asChild>
                <Button className="bg-brand-aqua hover:bg-brand-aqua-hover text-white" data-testid="add-supplier-btn"><Plus className="w-4 h-4 mr-1" />Add Supplier</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>New supplier</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Name</Label><Input value={sForm.name} onChange={e => setSForm({...sForm, name: e.target.value})} data-testid="supplier-name-input" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Contact person</Label><Input value={sForm.contact_person} onChange={e => setSForm({...sForm, contact_person: e.target.value})} /></div>
                    <div><Label>Category</Label>
                      <Select value={sForm.category} onValueChange={v => setSForm({...sForm, category: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="hardware">Hardware</SelectItem>
                          <SelectItem value="software">Software</SelectItem>
                          <SelectItem value="services">Services</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Email</Label><Input value={sForm.email} onChange={e => setSForm({...sForm, email: e.target.value})} /></div>
                    <div><Label>Phone</Label><Input value={sForm.phone} onChange={e => setSForm({...sForm, phone: e.target.value})} /></div>
                  </div>
                  <div><Label>Rating (1-5)</Label><Input type="number" min="1" max="5" value={sForm.rating} onChange={e => setSForm({...sForm, rating: e.target.value})} /></div>
                </div>
                <DialogFooter><Button onClick={createSupplier} className="bg-brand-aqua hover:bg-brand-aqua-hover text-white">Save</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <div className="table-wrap">
            <table className="w-full text-sm">
              <thead className="table-head"><tr><th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3">Category</th><th className="text-left px-4 py-3">Contact</th><th className="text-left px-4 py-3">Rating</th></tr></thead>
              <tbody>
                {suppliers.map(s => (
                  <tr key={s.id} className="row">
                    <td className="px-4 py-3 font-medium text-slate-800 dark:text-slate-100">{s.name}</td>
                    <td className="px-4 py-3"><Chip>{s.category}</Chip></td>
                    <td className="px-4 py-3 text-xs text-slate-500">{s.email}<br/>{s.phone}</td>
                    <td className="px-4 py-3"><div className="flex">{[1,2,3,4,5].map(n => <Star key={n} className={`w-3.5 h-3.5 ${n<=s.rating?"fill-amber-400 text-amber-400":"text-slate-200"}`}/>)}</div></td>
                  </tr>
                ))}
                {suppliers.length === 0 && <tr><td colSpan={4} className="text-center py-12 text-slate-500 text-sm">No suppliers yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="pos" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Dialog open={pOpen} onOpenChange={setPOpen}>
              <DialogTrigger asChild>
                <Button className="bg-brand-aqua hover:bg-brand-aqua-hover text-white" data-testid="add-po-btn"><Plus className="w-4 h-4 mr-1" />New PO</Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-2xl">
                <DialogHeader><DialogTitle>New purchase order</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Supplier</Label>
                      <Select value={pForm.supplier_id} onValueChange={v => setPForm({...pForm, supplier_id: v})}>
                        <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                      </Select></div>
                    <div><Label>Business Unit</Label>
                      <Select value={pForm.business_unit} onValueChange={v => setPForm({...pForm, business_unit: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="FigureWeb">FigureWeb</SelectItem>
                          <SelectItem value="FigureCX">FigureCX</SelectItem>
                          <SelectItem value="Telecom">Telecom</SelectItem>
                        <SelectItem value="FigureAcademy">Figure Academy</SelectItem>
                        </SelectContent>
                      </Select></div>
                  </div>
                  <div>
                    <Label>Line items</Label>
                    <div className="space-y-2 mt-1">
                      {pForm.items.map((it, i) => (
                        <div key={it._uid || `li-${i}`} className="grid grid-cols-[1fr,80px,120px] gap-2">
                          <Input placeholder="Description" value={it.description} onChange={e => updateItem(i, "description", e.target.value)} />
                          <Input type="number" value={it.quantity} onChange={e => updateItem(i, "quantity", e.target.value)} />
                          <Input type="number" placeholder="Rate" value={it.rate} onChange={e => updateItem(i, "rate", e.target.value)} />
                        </div>
                      ))}
                    </div>
                    <Button size="sm" variant="outline" onClick={() => setPForm({...pForm, items: [...pForm.items, { _uid: `li_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, description: "", quantity: 1, rate: 0 }]})} className="mt-2">
                      <Plus className="w-3.5 h-3.5 mr-1" />Add line
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-3 items-end">
                    <div><Label>Expected delivery</Label><Input type="date" value={pForm.expected_delivery} onChange={e => setPForm({...pForm, expected_delivery: e.target.value})} /></div>
                    <div className="text-right"><div className="text-xs text-slate-500">TOTAL</div><div className="font-heading font-bold text-xl num text-brand-aqua-hover">{formatNaira(poTotal)}</div></div>
                  </div>
                </div>
                <DialogFooter><Button onClick={createPO} className="bg-brand-aqua hover:bg-brand-aqua-hover text-white">Save</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <div className="table-wrap">
            <table className="w-full text-sm">
              <thead className="table-head"><tr><th className="text-left px-4 py-3">PO #</th><th className="text-left px-4 py-3">Supplier</th><th className="text-left px-4 py-3">Unit</th><th className="text-right px-4 py-3">Total</th><th className="text-left px-4 py-3">Status</th><th className="text-left px-4 py-3">Expected</th></tr></thead>
              <tbody>
                {pos.map(p => (
                  <tr key={p.id} className="row">
                    <td className="px-4 py-3 font-medium num text-slate-800 dark:text-slate-100">{p.po_number}</td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{p.supplier_name}</td>
                    <td className="px-4 py-3"><Chip>{p.business_unit}</Chip></td>
                    <td className="px-4 py-3 text-right font-heading font-semibold num">{formatNaira(p.total)}</td>
                    <td className="px-4 py-3"><Chip variant={statusChip(p.status)}>{p.status}</Chip></td>
                    <td className="px-4 py-3 text-xs text-slate-500">{formatDate(p.expected_delivery)}</td>
                  </tr>
                ))}
                {pos.length === 0 && <tr><td colSpan={6} className="text-center py-12 text-slate-500 text-sm">No purchase orders.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
